import {useCallback, useMemo} from 'react';
import {
    type AstView,
    findNodeById,
    positionsAreEqual,
    EnrichedAstNode,
    isNodeInsideBox,
    moveNodeToFrontOrBack,
    recalculateInnerPosition,
    checkIfHasSpacing,
    removePaddingFromNodes,
    removeMarginFromNodes,
    findAncestors,
} from 'rendition-core';

import type {RightClickMenuOptions, RightClickMenuOption} from 'Contexts/PreviewContext/RightClickMenuContext';
import {useRegrouping} from './useRegrouping';
import {useHighlighterContext} from 'Contexts/HighlighterContext';
// import {sendToBackgroundApiCall} from 'Common/api';

export const useRightClickMenuOptions = (
    view: AstView,
    setNewView: (view: AstView) => void,
    setResizeId: (id: string) => void,
    selectedNodes: EnrichedAstNode[],
    onSelectNodes: (ids: string[]) => void
) => {
    const {absolutePositionNodes, groupNodes, unGroupNode, removeNode, removeNodeAndChildren} =
        useRegrouping(onSelectNodes);

    const selectedNodeChildrenIds = useMemo(
        () => view.tree.children.map((child) => child.internalId),
        [view.tree.children]
    );

    const {highLightedNodeColors, highLightedNodes} = useHighlighterContext();

    const getIsPaddingVisible = useCallback(
        (id: string) => highLightedNodeColors[id]?.showPadding || highLightedNodes[id]?.includes('Padding'),
        [highLightedNodeColors, highLightedNodes]
    );

    const getIsMarginVisible = useCallback(
        (id: string) => highLightedNodeColors[id]?.showMargin || highLightedNodes[id]?.includes('Margin'),
        [highLightedNodeColors, highLightedNodes]
    );

    const clipContent = useCallback((view: AstView, id: string) => {
        const node = findNodeById(view.tree, id);
        if (!node || !node.visualPosition) {
            return;
        }
        node.position = {...node.position, ...node.visualPosition};
        recalculateInnerPosition(node);
    }, []);

    // const sendToBackground = useCallback(
    //     async (nodes: EnrichedAstNode[]) => {
    //         const [node] = nodes;
    //         const result = await sendToBackgroundApiCall(view, rootNode.internalId, node.internalId);
    //         setNewView(result);
    //     },
    //     [rootNode.internalId, setNewView, view]
    // );

    const getIsRoot = useCallback((node: EnrichedAstNode) => node.internalId === view.tree.internalId, [view]);

    const rightClickMenuOptions = useMemo<Array<RightClickMenuOptions>>(
        () => [
            // {
            //     label: 'Send to background',
            //     handler: sendToBackground,
            //     condition: (nodes) => {
            //         if (nodes.length > 1) {
            //             return false;
            //         }
            //         if (getIsRoot(nodes[0])) {
            //             return false;
            //         }
            //         return true;
            //     },
            // },
            {
                label: 'Group Selected Elements',
                handler: groupNodes,
                condition: (nodes) => nodes.length > 1,
            },
            {
                label: 'Ungroup Children',
                handler: unGroupNode,
                condition: (nodes) => {
                    return nodes.every((node) => {
                        if (getIsRoot(node)) {
                            return false;
                        }
                        if (node.children.length === 0) {
                            return false;
                        }

                        return true;
                    });
                },
            },
            {
                label: 'Absolute Position Element',
                handler: absolutePositionNodes,
                condition: (nodes) => {
                    return nodes.every((node) => {
                        if (getIsRoot(node)) {
                            return false;
                        }
                        if (!!node.inputLayoutAbstraction.absolutePosition || node.meta.systemGenerated) {
                            return false;
                        }

                        return true;
                    });
                },
            },
            {
                label: 'Un-Absolute Position Element',
                handler: (nodes) => {
                    nodes.forEach((node) => {
                        node.inputLayoutAbstraction.absolutePosition = false;
                        delete node.userInput.userSelectedParentId;
                    });
                    setNewView({...view});
                },
                condition: (nodes) => nodes.every((node) => !!node.inputLayoutAbstraction.absolutePosition),
            },
            {
                label: 'Remove Node',
                handler: removeNode,
                condition: (nodes) =>
                    nodes.every((node) => {
                        if (getIsRoot(node)) {
                            return false;
                        }
                        return !node.meta.systemGenerated;
                    }),
            },
            {
                label: 'Remove Node (With Children)',
                handler: removeNodeAndChildren,
                condition: (nodes) =>
                    nodes.every((node) => {
                        if (getIsRoot(node)) {
                            return false;
                        }
                        return node.children.length > 0;
                    }),
            },
            // {
            //     label: 'Resize Node',
            //     handler: (nodes) => {
            //         setResizeId(nodes[0].internalId);
            //     },
            //     condition: (nodes) => {
            //         if (nodes.length > 1) {
            //             return false;
            //         }
            //         return !getIsRoot(nodes[0]);
            //     },
            // },
            {
                label: 'Assign To Group',
                dropdown: true,
                handlers:
                    selectedNodes.length !== 1
                        ? []
                        : (findAncestors(selectedNodes[0], view.tree)
                              .map((ancestorId) => {
                                  const [node] = selectedNodes;
                                  const ancestor = findNodeById(view.tree, ancestorId);
                                  if (!ancestor) return null;
                                  return {
                                      label: ancestor?.uniqueName,
                                      condition: () => {
                                          const notSameNode = ancestor.internalId !== node.internalId;
                                          const notAbsolutePositionedNode =
                                              !ancestor.calculatedLayoutAbstraction?.absolutePosition;
                                          const {inside, overlap} = isNodeInsideBox(node, ancestor.position);

                                          return notSameNode && notAbsolutePositionedNode && (inside || overlap);
                                      },
                                      handler: (nodes) => {
                                          nodes.forEach((node) => {
                                              node.inputLayoutAbstraction.absolutePosition = true;
                                              node.userInput.userSelectedParentId = ancestorId;
                                              setNewView({...view});
                                          });
                                      },
                                  };
                              })
                              .filter(Boolean) as RightClickMenuOption[]),
                condition: (nodes) => {
                    if (nodes.length === 1) {
                        const [node] = nodes;
                        return node.styles.position === 'absolute' && selectedNodeChildrenIds.includes(node.internalId);
                    }
                    return false;
                },
            },
            {
                label: 'Move To Back',
                condition: (nodes) => nodes.every((node) => node.internalId !== view.tree.internalId),
                handler: (nodes) => {
                    nodes.forEach((node) => {
                        moveNodeToFrontOrBack(view, node, 'back');
                    });
                    setNewView({...view});
                },
            },
            {
                label: 'Move To Front',
                condition: (nodes) => nodes.every((node) => node.internalId !== view.tree.internalId),
                handler: (nodes) => {
                    nodes.forEach((node) => {
                        moveNodeToFrontOrBack(view, node, 'front');
                    });
                    setNewView({...view});
                },
            },
            {
                label: 'Clip Content',
                condition: (nodes) =>
                    nodes.every(
                        (node) => !!node.visualPosition && !positionsAreEqual(node.position, node.visualPosition)
                    ),
                handler: (nodes) => {
                    nodes.forEach((node) => {
                        clipContent(view, node.internalId);
                        setNewView({...view});
                    });
                },
            },

            {
                label: 'Remove Padding',
                dropdown: true,
                condition: (nodes) => nodes.every((node) => getIsPaddingVisible(node.internalId)),
                handlers: [
                    {
                        label: 'Remove all padding',
                        handler: (nodes) => removePaddingFromNodes(view, setNewView, nodes, ['all']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'padding', 'all')),
                    },
                    {
                        label: 'Remove vertical padding',
                        handler: (nodes) => removePaddingFromNodes(view, setNewView, nodes, ['y']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'padding', 'y')),
                    },
                    {
                        label: 'Remove horizontal padding',
                        handler: (nodes) => removePaddingFromNodes(view, setNewView, nodes, ['x']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'padding', 'x')),
                    },
                    {
                        label: 'Remove padding left',
                        handler: (nodes) => removePaddingFromNodes(view, setNewView, nodes, ['left']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'padding', 'left')),
                    },
                    {
                        label: 'Remove padding top',
                        handler: (nodes) => removePaddingFromNodes(view, setNewView, nodes, ['top']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'padding', 'top')),
                    },
                    {
                        label: 'Remove padding right',
                        handler: (nodes) => removePaddingFromNodes(view, setNewView, nodes, ['right']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'padding', 'right')),
                    },
                    {
                        label: 'Remove padding bottom',
                        handler: (nodes) => removePaddingFromNodes(view, setNewView, nodes, ['bottom']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'padding', 'bottom')),
                    },
                ],
            },
            {
                label: 'Remove Margin',
                dropdown: true,
                condition: (nodes) => nodes.every((node) => getIsMarginVisible(node.internalId)),
                handlers: [
                    {
                        label: 'Remove all margin',
                        handler: (nodes) => removeMarginFromNodes(view, setNewView, nodes, ['all']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'margin', 'all')),
                    },
                    {
                        label: 'Remove vertical margin',
                        handler: (nodes) => removeMarginFromNodes(view, setNewView, nodes, ['y']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'margin', 'y')),
                    },
                    {
                        label: 'Remove horizontal margin',
                        handler: (nodes) => removeMarginFromNodes(view, setNewView, nodes, ['x']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'margin', 'x')),
                    },
                    {
                        label: 'Remove margin left',
                        handler: (nodes) => removeMarginFromNodes(view, setNewView, nodes, ['left']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'margin', 'left')),
                    },
                    {
                        label: 'Remove margin top',
                        handler: (nodes) => removeMarginFromNodes(view, setNewView, nodes, ['top']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'margin', 'top')),
                    },
                    {
                        label: 'Remove margin right',
                        handler: (nodes) => removeMarginFromNodes(view, setNewView, nodes, ['right']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'margin', 'right')),
                    },
                    {
                        label: 'Remove margin bottom',
                        handler: (nodes) => removeMarginFromNodes(view, setNewView, nodes, ['bottom']),
                        condition: (nodes) =>
                            nodes.every((node) => checkIfHasSpacing(view, node.internalId, 'margin', 'bottom')),
                    },
                ],
            },
            {
                label: 'Remove Gap',
                handler: (nodes) => {
                    nodes.forEach((node) => {
                        node.innerPosition.gap = 0;
                    });
                    setNewView({...view});
                },
                condition: (nodes) => nodes.every((node) => node.innerPosition.gap),
            },
        ],
        [groupNodes, unGroupNode, absolutePositionNodes, removeNode, removeNodeAndChildren, selectedNodes, view, getIsRoot, setNewView, selectedNodeChildrenIds, clipContent, getIsPaddingVisible, getIsMarginVisible]
    );

    return rightClickMenuOptions;
};
